import { getTradeCommission } from '../config';
import { safePnl } from '../utils/formatters';
import { calcTradeStats } from '../services/statsCalculator';

/**
 * Custom hook that provides account stats and balance calculation functions.
 * Extracted from TradingJournal.js for modularization (Phase 2).
 *
 * Pure query/stats functions only — CRUD operations (addAccount, deleteAccount,
 * resetAccount, etc.) stay in TradingJournal.js until AccountsTab extraction.
 */
const useAccountManagement = ({ accounts, trades, includeResetTradesInStats, trackResetFees }) => {

  const getAccountById = (accountId) => {
    return accounts.find(acc => acc.id === accountId);
  };

  // Get trades for current period only (or all if includeResetTradesInStats is true)
  const getAccountTradesForStats = (accountId) => {
    const account = getAccountById(accountId);
    if (!account) return [];

    const accountTrades = trades.filter(t => t.accountId === accountId);

    if (includeResetTradesInStats) {
      return accountTrades;
    }

    // Only current period trades
    const currentPeriod = account.currentPeriod || 1;
    return accountTrades.filter(t => !t.period || t.period === currentPeriod);
  };

  // Get total reset fees for an account
  const getAccountResetFees = (accountId) => {
    const account = getAccountById(accountId);
    if (!account || !account.resetHistory) return 0;
    return account.resetHistory.reduce((sum, r) => sum + (r.fee || 0), 0);
  };

  // Get commission total for an account (uses centralized commission calculation)
  const getAccountCommissions = (accountId, forCurrentPeriodOnly = true) => {
    const account = getAccountById(accountId);
    if (!account) return 0;

    const currentPeriod = account.currentPeriod || 1;
    let accountTrades = trades.filter(t => t.accountId === accountId);

    if (forCurrentPeriodOnly) {
      accountTrades = accountTrades.filter(t => !t.period || t.period === currentPeriod);
    }

    // Use centralized commission calculation for each trade
    let totalCommissions = 0;
    accountTrades.forEach(trade => {
      totalCommissions += getTradeCommission(trade, account);
    });

    return totalCommissions;
  };

  const getAccountBalance = (accountId, forCurrentPeriodOnly = true) => {
    const account = getAccountById(accountId);
    if (!account) return 0;

    const currentPeriod = account.currentPeriod || 1;

    // Get trades for this account
    let accountTrades = trades.filter(t => t.accountId === accountId);

    // If only current period, filter out trades from previous periods
    if (forCurrentPeriodOnly) {
      accountTrades = accountTrades.filter(t => !t.period || t.period === currentPeriod);
    }

    const totalPnL = accountTrades.reduce((sum, t) => sum + safePnl(t), 0);

    // Calculate total commissions using symbol-specific rates and imported commissions
    const totalCommissions = getAccountCommissions(accountId, forCurrentPeriodOnly);

    // Subtract reset fees if tracking them and showing all-time
    let resetFees = 0;
    if (trackResetFees && !forCurrentPeriodOnly) {
      resetFees = getAccountResetFees(accountId);
    }

    // Include balance adjustment from manual sync
    const balanceAdjustment = account.balanceAdjustment || 0;

    const balance = account.startingBalance + totalPnL - totalCommissions - resetFees + balanceAdjustment;
    return isNaN(balance) ? account.startingBalance : balance;
  };

  const getAccountStats = (accountId) => {
    // Use period-aware function for stats
    const accountTrades = getAccountTradesForStats(accountId);
    const stats = calcTradeStats(accountTrades);

    // Include reset fees in total P&L if tracking them
    const account = getAccountById(accountId);
    let resetFees = 0;
    if (trackResetFees && includeResetTradesInStats && account) {
      resetFees = getAccountResetFees(accountId);
    }

    return {
      totalTrades: stats.totalTrades,
      totalPnL: stats.totalPnL - resetFees,
      wins: stats.wins,
      winRate: stats.winRate,
      resetFees
    };
  };

  // Calculate the highest EOD (End of Day) balance for an account in current period
  const getAccountHighBalance = (accountId) => {
    const account = getAccountById(accountId);
    if (!account) return account?.startingBalance || 50000;

    const currentPeriod = account.currentPeriod || 1;
    const startingBalance = account.startingBalance || 50000;
    const balanceAdjustment = account.balanceAdjustment || 0;
    const commissionPerSide = account.commissionPerSide || 0;

    // Get trades for this account in current period
    let accountTrades = trades.filter(t => t.accountId === accountId);
    accountTrades = accountTrades.filter(t => !t.period || t.period === currentPeriod);

    if (accountTrades.length === 0) return startingBalance + balanceAdjustment;

    // Group trades by date to calculate EOD balance for each day
    const tradesByDate = {};
    accountTrades.forEach(trade => {
      const date = trade.date;
      if (!tradesByDate[date]) {
        tradesByDate[date] = [];
      }
      tradesByDate[date].push(trade);
    });

    // Sort dates chronologically
    const sortedDates = Object.keys(tradesByDate).sort();

    // Calculate EOD balance for each day and track the highest
    let runningPnL = 0;
    let runningCommissions = 0;
    let highestEODBalance = startingBalance + balanceAdjustment;

    sortedDates.forEach(date => {
      const dayTrades = tradesByDate[date];
      dayTrades.forEach(trade => {
        runningPnL += safePnl(trade);
        const quantity = parseInt(trade.quantity) || 0;
        runningCommissions += quantity * 2 * commissionPerSide;
      });

      const eodBalance = startingBalance + runningPnL - runningCommissions + balanceAdjustment;
      if (eodBalance > highestEODBalance) {
        highestEODBalance = eodBalance;
      }
    });

    return highestEODBalance;
  };

  // Get the drawdown threshold for an account based on its drawdown type
  const getAccountDrawdownThreshold = (accountId) => {
    const account = getAccountById(accountId);
    if (!account) return null;

    const maxDrawdown = parseFloat(account.maxDrawdown) || 0;
    if (maxDrawdown === 0) return null;

    const drawdownType = account.drawdownType || 'static';
    const startingBalance = account.startingBalance || 50000;
    const balanceAdjustment = account.balanceAdjustment || 0;
    const manualThreshold = account.drawdownThreshold ? parseFloat(account.drawdownThreshold) : null;

    switch (drawdownType) {
      case 'static':
        // Static: use manual threshold if set, otherwise calculate from starting balance
        if (manualThreshold !== null) {
          return manualThreshold;
        }
        return startingBalance - maxDrawdown + balanceAdjustment;

      case 'eodTrailing': {
        // EOD Trailing: auto-calculate from highest EOD balance
        // The threshold trails UP as the account makes new highs
        const highBalance = getAccountHighBalance(accountId);
        const autoCalculatedThreshold = highBalance - maxDrawdown;

        // If user has manually set a threshold that's HIGHER than auto-calculated,
        // use the manual one (they might have synced from broker)
        if (manualThreshold !== null && manualThreshold > autoCalculatedThreshold) {
          return manualThreshold;
        }
        return autoCalculatedThreshold;
      }

      case 'liveTrailing': {
        // Live Trailing: use the manually set threshold
        // Fall back to current balance calculation if no manual threshold set
        if (manualThreshold !== null) {
          return manualThreshold;
        }
        const currentBalance = getAccountBalance(accountId);
        return currentBalance - maxDrawdown;
      }

      default:
        if (manualThreshold !== null) {
          return manualThreshold;
        }
        return startingBalance - maxDrawdown + balanceAdjustment;
    }
  };

  // Get cushion to drawdown (how much room before hitting threshold)
  const getAccountDrawdownCushion = (accountId) => {
    const threshold = getAccountDrawdownThreshold(accountId);
    if (threshold === null) return null;

    const currentBalance = getAccountBalance(accountId);
    return currentBalance - threshold;
  };

  return {
    getAccountById,
    getAccountTradesForStats,
    getAccountResetFees,
    getAccountCommissions,
    getAccountBalance,
    getAccountStats,
    getAccountHighBalance,
    getAccountDrawdownThreshold,
    getAccountDrawdownCushion
  };
};

export default useAccountManagement;
